// Course slice — Isomorphism Phase 2 · Blind Spot 1 (AppState slicing)
//
// Per-course state: draft, ingest result, progress, stale flag.
// Sub-routes (quiz, flashcards, etc.) READ these via selectors.
// Only CourseView (the gate component) WRITES these.
//
// Migration path: CourseView currently passes CourseCtx via React Context.
// The Zustand slice mirrors CourseCtx shape. During migration, CourseView
// writes to both Context AND Zustand; consumers gradually switch to Zustand.
// Once all consumers use Zustand, Context is removed.

import type { StateCreator } from 'zustand';
import type { CourseDraft, CourseProgress, IngestPackage } from '@examforge/shared/schemas';

export interface CourseSlice {
  // State
  /** Currently loaded course draft. Null when no course is loaded. */
  draft: CourseDraft | null;
  /** Generated ingest package (null until generation completes). */
  pkg: IngestPackage | null;
  /** User progress for the current course. */
  progress: CourseProgress | null;
  /** Whether the generated package is stale (source changed since generation). */
  isStale: boolean;

  // Actions
  setDraft: (draft: CourseDraft | null) => void;
  setPackage: (pkg: IngestPackage | null) => void;
  setProgress: (progress: CourseProgress | null) => void;
  setStale: (stale: boolean) => void;
  /** Reset all course state (navigating away from course). */
  resetCourse: () => void;

  // Gate-bound callbacks (set by CourseView, read by shell/sidebar/settings)
  /** Trigger regeneration (with confirmation dialog). Set by CourseView. */
  regenerate: (() => Promise<void>) | null;
  /** Switch to paste-JSON mode. Set by CourseView. */
  switchToPaste: (() => void) | null;
  /** Refresh progress from IDB. Set by CourseView. */
  refreshProgress: (() => Promise<void>) | null;
  /** Set the gate-bound callbacks (called by CourseView on mount/done). */
  setGateCallbacks: (cb: {
    regenerate: (() => Promise<void>) | null;
    switchToPaste: (() => void) | null;
    refreshProgress: (() => Promise<void>) | null;
  }) => void;
}

const initialCourseState = {
  draft: null as CourseDraft | null,
  pkg: null as IngestPackage | null,
  progress: null as CourseProgress | null,
  isStale: false,
  regenerate: null as (() => Promise<void>) | null,
  switchToPaste: null as (() => void) | null,
  refreshProgress: null as (() => Promise<void>) | null,
};

export const createCourseSlice: StateCreator<CourseSlice> = (set) => ({
  ...initialCourseState,

  setDraft: (draft) => set({ draft }),
  setPackage: (pkg) => set({ pkg }),
  setProgress: (progress) => set({ progress }),
  setStale: (isStale) => set({ isStale }),
  resetCourse: () => set(initialCourseState),
  setGateCallbacks: (cb) => set({
    regenerate: cb.regenerate,
    switchToPaste: cb.switchToPaste,
    refreshProgress: cb.refreshProgress,
  }),
});
